import React, { useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import { LuSearch, LuX } from 'react-icons/lu';

import type { SearchInputProps } from './types';
import { COMPONENTS_NAMESPACE } from '../../locales';
import { cn } from '../../utils/cn';

export type { SearchInputProps } from './types';

/**
 * A search input component with search icon and optional clear button.
 *
 * @param id - ID of the input element.
 * @param value - The current search value.
 * @param onChange - Callback fired when the value changes.
 * @param onSearch - Callback fired when search is submitted (Enter key).
 * @param onClear - Callback fired when clear button is clicked.
 * @param showClearButton - Whether to show clear button when value present (default: true).
 * @param placeholder - Placeholder text for the input (default: "Search...").
 * @param disabled - Whether the input is disabled.
 * @param className - Additional CSS classes for the container.
 * @param inputClassName - Additional CSS classes for the input element.
 * @param ...props - Additional HTML input attributes.
 *
 * @example
 * // Basic usage
 * <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} />
 *
 * @example
 * // With search and clear callbacks
 * <SearchInput
 *   value={search}
 *   onChange={(e) => setSearch(e.target.value)}
 *   onSearch={(value) => performSearch(value)}
 *   onClear={() => setSearch('')}
 * />
 *
 * @example
 * // Custom placeholder
 * <SearchInput placeholder="Suchen..." value={search} onChange={(e) => setSearch(e.target.value)} />
 */
export const SearchInput: React.FC<SearchInputProps> = ({
    className,
    disabled,
    id,
    inputClassName,
    onChange,
    onClear,
    onSearch,
    placeholder,
    showClearButton = true,
    value,
    ...props
}) => {
    const { t } = useTranslation(COMPONENTS_NAMESPACE);
    const hasValue = Boolean(value && String(value).length > 0);
    const resolvedPlaceholder = placeholder ?? t('searchInput.placeholder');

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter' && onSearch) {
                onSearch(String(value ?? ''));
            }
            props.onKeyDown?.(e);
        },
        [onSearch, value, props],
    );

    const handleClear = useCallback(() => {
        if (onClear) {
            onClear();
        } else if (onChange) {
            // Create a synthetic event to clear the value
            const syntheticEvent = {
                target: { value: '' },
            } as React.ChangeEvent<HTMLInputElement>;
            onChange(syntheticEvent);
        }
    }, [onClear, onChange]);

    return (
        <div
            className={cn(
                'h-8 w-full',
                'flex items-center gap-2',
                'px-3',
                'bg-white border border-grey-dark rounded',
                'focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-focus focus-within:ring-offset-1',
                disabled && 'cursor-not-allowed bg-surface-light opacity-50',
                className,
            )}
        >
            <span
                className={cn(
                    'flex items-center justify-center size-4 shrink-0',
                    'text-midnight',
                    disabled && 'opacity-50',
                )}
                aria-hidden="true"
            >
                <LuSearch className="size-4" />
            </span>
            <input
                id={id}
                type="text"
                disabled={disabled}
                value={value}
                onChange={onChange}
                onKeyDown={handleKeyDown}
                placeholder={resolvedPlaceholder}
                className={cn(
                    'flex-1 min-w-0 h-full',
                    'bg-transparent border-none outline-none',
                    'text-sm text-midnight',
                    'placeholder:text-midnight-placeholder',
                    'disabled:cursor-not-allowed',
                    inputClassName,
                )}
                {...props}
            />
            {showClearButton && hasValue && !disabled && (
                <button
                    type="button"
                    onClick={handleClear}
                    className={cn(
                        'flex items-center justify-center size-5 shrink-0',
                        'text-midnight opacity-50',
                        'hover:opacity-100 transition-opacity',
                        'focus:outline-none focus:ring-2 focus:ring-blue-focus focus:ring-offset-1 rounded',
                    )}
                    aria-label={t('searchInput.clearAriaLabel')}
                >
                    <LuX className="size-4" />
                </button>
            )}
        </div>
    );
};
